import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {Navigate, useNavigate, useLocation} from 'react-router';
import queryString from 'query-string';
import List from '@material-ui/core/List';
import {HiOutlineChartBar} from '@react-icons/all-files/hi/HiOutlineChartBar';
import isEmpty from 'lodash.isempty';
import get from 'lodash.get';
import {Card, CardHeader, CardBody} from '@components/shared/Card';
import {EmptyState} from '@components/shared/EmptyState';
import {Skeleton} from '@components/shared/Skeleton';
import {
    fetchSenderTransactionSummary,
    fetchTransactionsSummary,
    selectIsSenderSummaryFetching,
    selectIsTransactionsSummaryFetching,
    selectSenderSummary,
    selectSenderSummaryFetchingError,
    selectTransactionsSummary,
    selectTransactionsSummaryCount
} from '../../reportsSlice';
import {PER_PAGE_OPTIONS, REPORTS_PATH, TRANSACTION_SUMMARY_STATUSES} from '../../../../../constants';
import TypeSelector from './TypeSelector/TypeSelector';
import TransactionSummary from './TransactionSummary/TransactionSummary';
import PagePagination from '../../../shared/Pagination/Pagination';
import PageTitle from '../../../shared/PageTitle';
import Loading from '../../../shared/Loading/Loading';
import useDateFormats from '../../../../hooks/useDateFormats';
import './senderSummary.less';

const TRANSACTION_SKELETON_COUNT = 3;

function getStatusToShow(summary = {}, params) {
    if (!isEmpty(summary.packages)) {
        if (params && params.status && summary.packages[params.status] > 0) {
            return params.status;
        }
        return TRANSACTION_SUMMARY_STATUSES
            .find((transactionStatus) => summary.packages[transactionStatus] && summary.packages[transactionStatus] > 0);
    }
    return '';
}

function getSenderSummary(summaries = [], senderId = '') {
    if (!isEmpty(summaries)) {
        return summaries.find((summary) => summary?.sender?.id === senderId) || {};
    }
    return {};
}

export default function SenderSummary() {
    const intl = useIntl();
    const {getLongDate} = useDateFormats();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const params = queryString.parse(location.search);
    const [from, setFrom] = useState(params.from || 1);
    const [to, setTo] = useState(params.to || 20);
    const [perPage, setPerPage] = useState(PER_PAGE_OPTIONS['20']);
    const sendersSummary = useSelector(selectSenderSummary);
    const transactionsSummary = useSelector(selectTransactionsSummary);
    const transactionsSummaryCount = useSelector(selectTransactionsSummaryCount);
    const isSenderSummaryFetching = useSelector(selectIsSenderSummaryFetching);
    const isTransactionsSummaryFetching = useSelector(selectIsTransactionsSummaryFetching);
    const senderSummaryFetchingError = useSelector(selectSenderSummaryFetchingError);
    const selectedSummary = getSenderSummary(sendersSummary, params.senderId);
    const statusToShow = getStatusToShow(selectedSummary, params);
    const [status, setStatus] = useState(statusToShow);
    const hasParams = !!params.startDate && !!params.endDate && !!params.senderId;
    const startDateLabel = getLongDate(params.startDate);
    const endDateLabel = getLongDate(params.endDate);
    const senderFullName = `${get(selectedSummary, 'sender.firstName', '')} ${get(selectedSummary, 'sender.lastName', '')}`;
    const pageTitle = intl.formatMessage({id: 'esl.partials.reports.transactions_for', defaultMessage: ''}, {senderName: senderFullName});
    const subTitle = intl.formatMessage({id: 'esl.generic.account_summary_report', defaultMessage: ''});
    const hasTransactions = !isEmpty(transactionsSummary);
    const isLoading = isSenderSummaryFetching || isTransactionsSummaryFetching;

    async function fetchTransactions(summary, options) {
        if (!isEmpty(summary) && !isEmpty(summary.packages)) {
            dispatch(fetchTransactionsSummary(options));
        }
    }

    async function fetchSenderTransaction() {
        if (isEmpty(selectedSummary) && !isSenderSummaryFetching && !senderSummaryFetchingError && hasParams) {
            const senderTransactionsSummary = await dispatch(fetchSenderTransactionSummary({
                senderId: params.senderId,
                startDate: params.startDate,
                endDate: params.endDate,
                status
            }));
            const senderSummary = getSenderSummary(senderTransactionsSummary, params.senderId);
            const transactionsStatus = getStatusToShow(senderSummary, params);

            fetchTransactions(senderSummary, {
                senderId: params.senderId,
                startDate: params.startDate,
                endDate: params.endDate,
                status: transactionsStatus
            });
            if (status !== transactionsStatus) {
                setStatus(transactionsStatus);
            }
        } else {
            const transactionsStatus = getStatusToShow(selectedSummary, params);

            fetchTransactions(selectedSummary, {
                senderId: params.senderId,
                startDate: params.startDate,
                endDate: params.endDate,
                status: getStatusToShow(selectedSummary, params)
            });
            if (status !== transactionsStatus) {
                setStatus(transactionsStatus);
            }
        }
    }

    function changeStatus(changedStatus) {
        const query = queryString.stringify({
            ...params,
            status: changedStatus
        });
        navigate(`${location.pathname}?${query}`, {replace: true});
        setStatus(changedStatus);
        fetchTransactions(selectedSummary, {
            ...params,
            status: changedStatus
        });
    }

    function changePage(options) {
        setFrom(options.from);
        setTo(options.to);

        fetchTransactions(selectedSummary, {
            ...params,
            ...options
        });
    }

    function changePerPage(changedPerPage) {
        setPerPage(changedPerPage);
    }

    useEffect(() => {
        fetchSenderTransaction();
    }, []);

    function renderSkeletonItems() {
        return Array.from({length: TRANSACTION_SKELETON_COUNT}).map((_, index) => (
            <div key={`tx-skeleton-${index}`} className="transaction-summary-skeleton">
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="rect" height="2rem" />
            </div>
        ));
    }

    return (
        <div className="sender-summary">
            <Loading isLoading={isLoading} />
            {!hasParams && <Navigate to={`${REPORTS_PATH}${location.search}`} />}
            <PageTitle pageTitle={pageTitle} />
            <Card padding="md" elevation={1}>
                <CardHeader title={pageTitle} description={subTitle} />
                <CardBody>
                    <div className="sender-summary-header">
                        <TypeSelector
                            value={status}
                            changeStatus={changeStatus}
                            packages={selectedSummary.packages}
                        />
                        <div className="date-range">
                            <span className="date-range-item">
                                {`${intl.formatMessage({id: 'esl.generic.from', defaultMessage: ''})}: ${startDateLabel}`}
                            </span>
                            <span className="date-range-item">
                                {`${intl.formatMessage({id: 'esl.generic.to', defaultMessage: ''})}: ${endDateLabel}`}
                            </span>
                        </div>
                    </div>
                    <div className="transactions-summary-container">
                        {isLoading && !hasTransactions && renderSkeletonItems()}
                        {!isLoading && hasTransactions && (
                            <List classes={{root: 'transactions-summary'}}>
                                {transactionsSummary.map((transaction) => (
                                    <TransactionSummary transaction={transaction} key={transaction.id} />
                                ))}
                            </List>
                        )}
                        {!isLoading && !hasTransactions && (
                            <EmptyState
                                icon={<HiOutlineChartBar />}
                                title={intl.formatMessage({id: 'esl.reports.no_transactions_title', defaultMessage: 'No transactions to show'})}
                                description={intl.formatMessage({id: 'esl.reports.no_transactions_description', defaultMessage: 'There are no transactions for this status in the selected date range.'})}
                                size="sm"
                            />
                        )}
                    </div>
                    {transactionsSummaryCount > perPage && (
                        <div className="sender-summary-pagination-container">
                            <PagePagination
                                from={from}
                                to={to}
                                total={transactionsSummaryCount}
                                perPage={perPage}
                                className="sender-summary-pagination"
                                changePage={changePage}
                                changePerPage={changePerPage}
                            />
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
}
